import { For, type JSX } from 'solid-js';
import { t } from '../i18n';
import type { ApiArtifact } from '../lib/artifacts';
import { absolute, count, decimal } from '../lib/time';
import { UNSET } from '../lib/unset';
import { DataTable, type DataTableColumn } from './DataTable';
import { Button } from './ui/Button';

// The account's artifacts as a data table (spec §3.15): its id (the address every
// surface takes), type + size, visibility, the coordinates it was produced under,
// when it was made and when it expires, then Download / Share / Delete.
// Presenter — the route owns the filters, the re-reads and every call.
//
// The id is rendered in FULL, not elided: it is the argument `artifact get` and
// `GET /v1/artifacts/:id/url` take, so a reader copying it out of this table is
// the point of showing it.
//
// There is no `key` column and there never can be — the API's `ArtifactView` has
// no such field. A presigned URL is the only path to the bytes.

/** Each visibility in words — literal `t()` calls, so the i18n coverage scan sees every key. */
const VISIBILITY_LABEL = {
  run: () => t('artifacts.visibility_run'),
  account: () => t('artifacts.visibility_account'),
  'public-link': () => t('artifacts.visibility_public_link'),
} as const satisfies Record<ApiArtifact['visibility'], () => string>;

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'] as const;

/**
 * Size for humans. `null` = the producer never declared one, not "zero bytes".
 * The value is rounded to the one decimal it renders with BEFORE the unit is
 * settled: picking the unit first printed 1,048,550 B as "1024.0 KB".
 */
export function formatBytes(size: number | null): string {
  if (size === null) return UNSET;
  if (size < 1024) return `${count(size)} B`;
  let n = size;
  let i = 0;
  while (i < BYTE_UNITS.length - 1 && Math.round(n * 10) / 10 >= 1024) {
    n /= 1024;
    i += 1;
  }
  return `${decimal(n)} ${BYTE_UNITS[i]}`;
}

/**
 * Where this artifact came from — the three filterable coordinates plus the
 * server-derived producer, each shown only when the row carries it. `created_by`
 * belongs here rather than in its own column: it is the same kind of fact, and it
 * is `run:<id>` / `user:<tokenId>`, never a name.
 */
export function artifactSources(
  artifact: ApiArtifact,
): readonly { readonly label: string; readonly id: string }[] {
  return [
    { label: t('artifacts.source_run'), id: artifact.run_id },
    { label: t('artifacts.source_task'), id: artifact.task_id },
    { label: t('artifacts.source_repo'), id: artifact.repo_id },
    { label: t('artifacts.source_by'), id: artifact.created_by },
  ].flatMap((s) => (s.id === null ? [] : [{ label: s.label, id: s.id }]));
}

export function ArtifactTable(props: {
  readonly rows: readonly ApiArtifact[];
  readonly isBusy: (id: string) => boolean;
  readonly onDownload: (artifact: ApiArtifact) => void;
  readonly onShare: (artifact: ApiArtifact) => void;
  readonly onDelete: (artifact: ApiArtifact) => void;
}): JSX.Element {
  const shared = (a: ApiArtifact): boolean => a.visibility === 'public-link';
  const actions = (a: ApiArtifact): JSX.Element => (
    <div class="data-table__actions">
      <Button
        variant="ghost"
        size="sm"
        disabled={props.isBusy(a.id)}
        onClick={() => props.onDownload(a)}
      >
        {t('artifacts.download')}
      </Button>
      {/* Already `public-link`: the flip has happened, so the label says the
          click mints a fresh link rather than claiming to publish it again. */}
      <Button
        variant="ghost"
        size="sm"
        disabled={props.isBusy(a.id)}
        onClick={() => props.onShare(a)}
      >
        {t(shared(a) ? 'artifacts.relink' : 'artifacts.share')}
      </Button>
      <Button
        variant="ghost"
        size="sm"
        disabled={props.isBusy(a.id)}
        onClick={() => props.onDelete(a)}
      >
        {t('artifacts.delete')}
      </Button>
    </div>
  );

  const columns: readonly DataTableColumn<ApiArtifact>[] = [
    { key: 'id', rowHeader: true, header: t('artifacts.col_id'), mono: true, cell: (a) => a.id },
    { key: 'type', header: t('artifacts.col_type'), mono: true, cell: (a) => a.content_type },
    { key: 'size', header: t('artifacts.col_size'), cell: (a) => formatBytes(a.size) },
    {
      key: 'visibility',
      header: t('artifacts.col_visibility'),
      cell: (a) => (
        <span classList={{ badge: true, 'badge--outline': !shared(a) }}>
          {VISIBILITY_LABEL[a.visibility]()}
        </span>
      ),
    },
    {
      key: 'source',
      header: t('artifacts.col_source'),
      mono: true,
      cell: (a) => (
        <For each={artifactSources(a)}>
          {(source) => (
            <div>
              {source.label} {source.id}
            </div>
          )}
        </For>
      ),
    },
    { key: 'created', header: t('artifacts.col_created'), cell: (a) => absolute(a.created_iso) },
    {
      key: 'expires',
      header: t('artifacts.col_expires'),
      cell: (a) =>
        a.expires_iso === null ? t('artifacts.never_expires') : absolute(a.expires_iso),
    },
    {
      key: 'actions',
      header: t('artifacts.col_actions'),
      srOnlyHeader: true,
      align: 'end',
      cell: actions,
    },
  ];

  return (
    <DataTable
      label={t('artifacts.table_label')}
      columns={columns}
      rows={props.rows}
      rowKey={(a) => a.id}
    />
  );
}
